import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
} from "@mui/material";
import SoftButton from "components/SoftButton";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import swal from "sweetalert2";
import { api } from "utils/Api";
import { showWarning } from "utils/messageAlerts";
import { showSuccess } from "utils/messageAlerts";

function AdminPropertiesEditForm({
  onClose,
  mode,
  property,
  refreshProperties,
  organization_id,
}) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    property_code: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pin_code: "",
    active: true,
    manager_email: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === "edit" && property) {
      setForm({
        name: property.name || "",
        description: property.description || "",
        property_code: property.property_code || "",
        address: property.address || "",
        city: property.city || "",
        state: property.state || "",
        country: property.country || "",
        pin_code: property.pin_code || "",
        active: property.active ?? true,
        manager_email: "",
      });
    }
  }, [mode, property]);

  const handleChange = (field, value) => {
    let newValue = value;
    if (field === "property_code") { newValue = value.toUpperCase(); }
    if (field === "pin_code") { newValue = value.toUpperCase().replace(/[^A-Z0-9 ]/g, ""); }
    setForm((prev) => ({ ...prev, [field]: newValue }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Required";
    if (!form.property_code.trim()) newErrors.property_code = "Required";
    if (!form.address.trim()) newErrors.address = "Required";
    if (!form.city.trim()) newErrors.city = "Required";
    if (!form.state.trim()) newErrors.state = "Required";
    if (!form.country.trim()) newErrors.country = "Required";
    if (!form.pin_code.trim()) newErrors.pin_code = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const code = form.property_code.trim().toUpperCase();

    const propertyCodeRegex = /^(?!.*-.*-)[A-Z0-9-]/;

    if (!propertyCodeRegex.test(code)) {
      showWarning("Property code must be 6 characters, can include letters, numbers, and at most one hyphen (not at the start or end).");
      return;
    }

    const payload = {
      ...form,
      name: form.name.trim(),
      description: form.description.trim(),
      property_code: code,
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      country: form.country.trim(),
      pin_code: form.pin_code.trim(),
      organization_id: Number(organization_id),
    };

    try {
      setLoading(true);

      let propertyId;

      if (mode === "create") {
        const response = await api.properties.createProperty(payload);
        propertyId = response.id;
        showSuccess("Property created successfully");
      } else {
        await api.properties.updatePropertyDetails(property.id, payload);
        propertyId = property.id;
        showSuccess("Property updated successfully");
      }

      refreshProperties();

      // if (form.manager_email?.trim()) {
      //   try {
      //     await api.assign_user_property.assignUserToProperty({
      //       property_id: propertyId,
      //       email: form.manager_email.trim(),
      //       role_id: 3,
      //     });

      //     showSuccess("Manager assigned successfully");
      //   } catch (error) {
          
      //   }
      // }

      onClose();
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{mode === "create" ? "Create Property" : "Edit Property"}</DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Field
              label="Property Name"
              value={form.name}
              error={errors.name}
              onChange={(v) => handleChange("name", v)}
              required
            />

            <Field
              label="Property Code (EX: ABC-12, ABC123, ABC-1A)"
              value={form.property_code}
              error={errors.property_code}
              onChange={(v) => handleChange("property_code", v)}
              required
              inputProps={{ maxLength: 6 }}
            />

            <Field
              label="Description"
              value={form.description}
              onChange={(v) => handleChange("description", v)}
              fullWidth
            />

            <Field
              label="Address"
              value={form.address}
              error={errors.address}
              onChange={(v) => handleChange("address", v)}
              fullWidth
              required
            />

            <Field
              label="City"
              value={form.city}
              error={errors.city}
              onChange={(v) => handleChange("city", v)}
              required
            />

            <Field
              label="State"
              value={form.state}
              error={errors.state}
              onChange={(v) => handleChange("state", v)}
              required
            />

            <Field
              label="Country"
              value={form.country}
              error={errors.country}
              onChange={(v) => handleChange("country", v)}
              required
            />

            <Field
              label="PIN Code"
              type="text"
              value={form.pin_code}
              error={errors.pin_code}
              onChange={(v) => handleChange("pin_code", v)}
              required
              inputProps={{ maxLength: 10 }}
            />

            {/* {mode === "create" && (
              <Field
                label="Manager Email"
                value={form.manager_email}
                onChange={(v) => handleChange("manager_email", v)}
                fullWidth
              />
            )} */}
          </Grid>
        </DialogContent>

        <DialogActions>
          <SoftButton onClick={onClose}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="dark" disabled={loading} type="submit">
            {loading ? "Saving..." : "Save"}
          </SoftButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  required,
  type = "text",
  fullWidth,
  inputProps = {},
}) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <SoftTypography variant="caption" fontWeight="bold">
        {label}
        {required && (
          <Box component="span" color="error.main">
            {" "}
            *
          </Box>
        )}
      </SoftTypography>

      <SoftInput
        type={type}
        placeholder={`Enter ${label}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        inputProps={inputProps}
      />

      {error && (
        <SoftTypography variant="caption" color="error">
          {error}
        </SoftTypography>
      )}
    </Grid>
  );
}

Field.propTypes = {
label: PropTypes.string.isRequired,
value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
onChange: PropTypes.func.isRequired,
error: PropTypes.string,
required: PropTypes.bool,
type: PropTypes.string,
fullWidth: PropTypes.bool,
inputProps: PropTypes.object,
};

AdminPropertiesEditForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(["create", "edit"]).isRequired,
    property: PropTypes.object,
    refreshProperties: PropTypes.func.isRequired,
    organization_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default AdminPropertiesEditForm;